import {
  del,
  get,
  patch,
  post,
  type RequestConfig,
} from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import type {
  DonationMasterNamePayload,
  DonationMasterNameRecord,
  DonationSubCategoryPayload,
  DonationSubCategoryRecord,
} from "@project/types/donation-master";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  unwrapItem,
} from "./list-api-helpers";

const SILENT: RequestConfig = { showNotification: false };

const ENDPOINTS = {
  donorType: `${JIVANDHARA_BASE}/donor-type-master`,
  donorCategory: `${JIVANDHARA_BASE}/donor-category-master`,
  donationType: `${JIVANDHARA_BASE}/donation-type-master`,
  mainCategory: `${JIVANDHARA_BASE}/donation-main-category-master`,
  subCategory: `${JIVANDHARA_BASE}/donation-sub-category-master`,
} as const;

function createNameMasterService(base: string) {
  return {
    listPage: (query: ListQuery = {}) =>
      fetchListPage<DonationMasterNameRecord>(`${base}/`, query),

    get: async (id: number) => {
      const data = await get<
        ApiItemResponse<DonationMasterNameRecord> | DonationMasterNameRecord
      >(`${base}/${id}/`, SILENT_LIST);
      return unwrapItem(data);
    },

    create: async (payload: DonationMasterNamePayload) => {
      const data = await post<
        ApiItemResponse<DonationMasterNameRecord> | DonationMasterNameRecord,
        DonationMasterNamePayload
      >(`${base}/`, payload, SILENT);
      const item = unwrapItem(data);
      if (!item) {
        throw new Error("Failed to create record.");
      }
      return item;
    },

    update: async (id: number, payload: Partial<DonationMasterNamePayload>) => {
      const data = await patch<
        ApiItemResponse<DonationMasterNameRecord> | DonationMasterNameRecord,
        Partial<DonationMasterNamePayload>
      >(`${base}/${id}/`, payload, SILENT);
      return unwrapItem(data);
    },

    delete: async (id: number) => {
      await del(`${base}/${id}/`, SILENT);
    },
  };
}

class DonationMasterService {
  donorType = createNameMasterService(ENDPOINTS.donorType);
  donorCategory = createNameMasterService(ENDPOINTS.donorCategory);
  donationType = createNameMasterService(ENDPOINTS.donationType);
  mainCategory = createNameMasterService(ENDPOINTS.mainCategory);

  listSubCategoriesPage = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<DonationSubCategoryRecord>> =>
    fetchListPage<DonationSubCategoryRecord>(
      `${ENDPOINTS.subCategory}/`,
      query
    );

  getSubCategory = async (id: number) => {
    const data = await get<
      ApiItemResponse<DonationSubCategoryRecord> | DonationSubCategoryRecord
    >(`${ENDPOINTS.subCategory}/${id}/`, SILENT_LIST);
    return unwrapItem(data);
  };

  createSubCategory = async (payload: DonationSubCategoryPayload) => {
    const data = await post<
      ApiItemResponse<DonationSubCategoryRecord> | DonationSubCategoryRecord,
      DonationSubCategoryPayload
    >(`${ENDPOINTS.subCategory}/`, payload, SILENT);
    const item = unwrapItem(data);
    if (!item) {
      throw new Error("Failed to create sub category.");
    }
    return item;
  };

  updateSubCategory = async (
    id: number,
    payload: Partial<DonationSubCategoryPayload>
  ) => {
    const data = await patch<
      ApiItemResponse<DonationSubCategoryRecord> | DonationSubCategoryRecord,
      Partial<DonationSubCategoryPayload>
    >(`${ENDPOINTS.subCategory}/${id}/`, payload, SILENT);
    return unwrapItem(data);
  };

  deleteSubCategory = async (id: number) => {
    await del(`${ENDPOINTS.subCategory}/${id}/`, SILENT);
  };

  listMainCategoriesForLookup = async (query = "") => {
    const result = await this.mainCategory.listPage({
      page: 1,
      page_size: 50,
      search: query || undefined,
      status: "active",
    });
    return result.data.map(item => ({
      value: String(item.id),
      label: item.name,
    }));
  };

  listDonationTypesForLookup = async (query = "") => {
    const result = await this.donationType.listPage({
      page: 1,
      page_size: 50,
      search: query || undefined,
      status: "active",
    });
    return result.data.map(item => ({
      value: String(item.id),
      label: item.name,
    }));
  };

  listSubCategoriesForLookup = async (
    mainCategoryId: number | string | undefined,
    query = ""
  ) => {
    if (!mainCategoryId) {
      return [];
    }
    const result = await this.listSubCategoriesPage({
      page: 1,
      page_size: 50,
      search: query || undefined,
      status: "active",
      main_category: Number(mainCategoryId),
    });
    return result.data.map(item => ({
      value: String(item.id),
      label: item.title,
    }));
  };
}

export const donationMasterService = new DonationMasterService();
